import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDarkMode } from "../../context/DarkModeContext";
import {
  BookOpen,
  Search,
  FileText,
  User,
  Shield,
  Bell,
  CreditCard,
  // Smartphone,
  HelpCircle,
  Users,
  Mail,
  CheckCircle,
  // XCircle,
  ArrowRight,
  Home,
  Settings,
  Download,
  // Upload,
  // Filter,
  // AlertCircle,
  // Smartphone as Mobile,
  Monitor,
} from "lucide-react";

// Types
interface Guide {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  category: string;
  content: string;
}

interface GuideCategory {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const Guides: React.FC = () => {
  const { isDark } = useDarkMode();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] =
    useState<string>("getting-started");
  const [selectedGuide, setSelectedGuide] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Guide categories
  const categories: GuideCategory[] = [
    {
      id: "getting-started",
      title: "Getting Started",
      description: "Account setup and basic platform usage",
      icon: <Home size={20} />,
    },
    {
      id: "profile-management",
      title: "Profile & Account",
      description: "Managing your profile and security settings",
      icon: <User size={20} />,
    },
    {
      id: "job-search",
      title: "Job Search",
      description: "Advanced search techniques and alerts",
      icon: <Search size={20} />,
    },
    {
      id: "applications",
      title: "Applications",
      description: "Applying and tracking job applications",
      icon: <FileText size={20} />,
    },
    {
      id: "resumes",
      title: "Resumes & Documents",
      description: "Managing your resumes and documents",
      icon: <Download size={20} />,
    },
    {
      id: "subscription",
      title: "Subscription & Billing",
      description: "Managing your subscription and payments",
      icon: <CreditCard size={20} />,
    },
    {
      id: "technical",
      title: "Technical Help",
      description: "Browser compatibility and troubleshooting",
      icon: <Settings size={20} />,
    },
  ];

  // All guides (content remains the same as in your original code)
  const guides: Guide[] = [
    // Getting Started
    {
      id: "how-to-create-and-verify-your-account",
      title: "How to Create and Verify Your Account",
      description: "Step-by-step guide to setting up your account",
      icon: <User size={16} />,
      category: "getting-started",
      content: `
        <h3>Creating Your Account</h3>
        <ol>
          <li>Click the "Sign Up" button in the top right corner</li>
          <li>Enter your email address and create a strong password</li>
          <li>Fill in your basic information (name, location, etc.)</li>
          <li>Agree to our Terms of Service and Privacy Policy</li>
          <li>Click "Create Account"</li>
        </ol>
        
        <h3>Verifying Your Account</h3>
        <ol>
          <li>Check your email for a verification link</li>
          <li>Click the verification link within 24 hours</li>
          <li>If you didn't receive the email, check your spam folder</li>
          <li>You can request a new verification email from your dashboard</li>
        </ol>
        
        <div class="bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 p-4 rounded-lg mt-4">
          <strong>Tip:</strong> Use a professional email address that you check regularly for job-related communications.
        </div>
      `,
    },

    // Profile Management
    {
      id: "updating-your-profile-information",
      title: "Updating Your Profile Information",
      description: "How to keep your profile current and complete",
      icon: <User size={16} />,
      category: "profile-management",
      content: `
        <h3>Accessing Your Profile</h3>
        <p>Navigate to your Profile page from the dashboard sidebar.</p>
        
        <h3>Editable Profile Sections</h3>
        <ul>
          <li><strong>Personal Information:</strong> Name, contact details, location</li>
          <li><strong>Professional Summary:</strong> Your career objective and overview</li>
          <li><strong>Work Experience:</strong> Add, edit, or remove work history</li>
          <li><strong>Education:</strong> Academic background and qualifications</li>
          <li><strong>Skills:</strong> Technical and soft skills with proficiency levels</li>
          <li><strong>Certifications:</strong> Professional certifications and licenses</li>
        </ul>
        
        <h3>Best Practices</h3>
        <ul>
          <li>Keep your profile updated with recent experience</li>
          <li>Use industry-specific keywords for better visibility</li>
          <li>Upload a professional profile picture</li>
          <li>Ensure contact information is current</li>
        </ul>
      `,
    },
    {
      id: "managing-privacy-and-security-settings",
      title: "Managing Privacy and Security Settings",
      description: "Control your data privacy and account security",
      icon: <Shield size={16} />,
      category: "profile-management",
      content: `
        <h3>Privacy Settings</h3>
        <ul>
          <li><strong>Profile Visibility:</strong> Control who can see your profile</li>
          <li><strong>Application Privacy:</strong> Choose if employers can see your full profile</li>
          <li><strong>Data Sharing:</strong> Manage how your data is used for job matching</li>
        </ul>
        
        <h3>Security Settings</h3>
        <ul>
          <li><strong>Password Management:</strong> Change your password regularly</li>
          <li><strong>Two-Factor Authentication:</strong> Enable for extra security</li>
          <li><strong>Login Alerts:</strong> Get notified of new sign-ins</li>
          <li><strong>Session Management:</strong> View and manage active sessions</li>
        </ul>
      `,
    },

    // Job Search
    {
      id: "advanced-job-search-techniques",
      title: "Advanced Job Search Techniques",
      description: "Master the search functionality to find perfect jobs",
      icon: <Search size={16} />,
      category: "job-search",
      content: `
        <h3>Boolean Search Operators</h3>
        <p>Use these operators to refine your search:</p>
        <ul>
          <li><code>AND</code> - Find jobs that include all specified terms</li>
          <li><code>OR</code> - Find jobs that include any of the specified terms</li>
          <li><code>NOT</code> - Exclude jobs with specific terms</li>
          <li><code>" "</code> - Search for exact phrases</li>
          <li><code>( )</code> - Group search terms together</li>
        </ul>
        
        <h3>Advanced Filters</h3>
        <ul>
          <li><strong>Salary Range:</strong> Filter by minimum and maximum salary</li>
          <li><strong>Experience Level:</strong> Entry, Mid, Senior, Executive</li>
          <li><strong>Job Type:</strong> Full-time, Part-time, Contract, Remote</li>
          <li><strong>Company Size:</strong> Startup, Small, Medium, Large</li>
          <li><strong>Posted Date:</strong> Last 24 hours, 3 days, week, month</li>
        </ul>
      `,
    },
    {
      id: "setting-up-job-alerts",
      title: "Setting Up Job Alerts",
      description: "Get notified about new jobs matching your criteria",
      icon: <Bell size={16} />,
      category: "job-search",
      content: `
        <h3>Creating Job Alerts</h3>
        <ol>
          <li>Perform a search with your desired criteria</li>
          <li>Click "Save Search" or "Create Alert"</li>
          <li>Name your alert for easy reference</li>
          <li>Choose notification frequency (daily, weekly, instant)</li>
          <li>Select delivery method (email, push notification)</li>
        </ol>
        
        <h3>Managing Alerts</h3>
        <ul>
          <li>Edit alert criteria from your dashboard</li>
          <li>Pause alerts when you're not actively looking</li>
          <li>Delete alerts that are no longer relevant</li>
          <li>Set different alerts for different job types</li>
        </ul>
      `,
    },

    // Applications
    {
      id: "writing-effective-cover-letters",
      title: "Writing Effective Cover Letters",
      description: "Craft compelling cover letters that get noticed",
      icon: <FileText size={16} />,
      category: "applications",
      content: `
        <h3>Cover Letter Structure</h3>
        <ol>
          <li><strong>Header:</strong> Your contact information and date</li>
          <li><strong>Salutation:</strong> Address the hiring manager by name</li>
          <li><strong>Opening Paragraph:</strong> State the position and your interest</li>
          <li><strong>Body Paragraphs:</strong> Connect your experience to the job requirements</li>
          <li><strong>Closing Paragraph:</strong> Express enthusiasm and call to action</li>
          <li><strong>Signature:</strong> Professional closing and your name</li>
        </ol>
        
        <h3>Best Practices</h3>
        <ul>
          <li>Customize each letter for the specific job</li>
          <li>Use the company's language and values</li>
          <li>Highlight 2-3 key achievements relevant to the role</li>
          <li>Keep it concise (less than one page)</li>
          <li>Proofread carefully for errors</li>
        </ul>
      `,
    },
    {
      id: "tracking-application-status",
      title: "Tracking Application Status",
      description: "Monitor and manage your job applications",
      icon: <CheckCircle size={16} />,
      category: "applications",
      content: `
        <h3>Application Status Types</h3>
        <ul>
          <li><strong>Submitted:</strong> Application received by employer</li>
          <li><strong>Under Review:</strong> Employer is reviewing your application</li>
          <li><strong>Shortlisted:</strong> You've been selected for further consideration</li>
          <li><strong>Interview:</strong> Scheduled for interview</li>
          <li><strong>Offer:</strong> Job offer extended</li>
          <li><strong>Rejected:</strong> Not selected for the position</li>
          <li><strong>Withdrawn:</strong> You withdrew your application</li>
        </ul>
        
        <h3>Managing Applications</h3>
        <ul>
          <li>View all applications in your dashboard</li>
          <li>Filter by status, date, or company</li>
          <li>Set follow-up reminders</li>
          <li>Export application history for tracking</li>
        </ul>
      `,
    },

    // Resumes
    {
      id: "creating-an-ats-friendly-resume",
      title: "Creating an ATS-Friendly Resume",
      description: "Optimize your resume for Applicant Tracking Systems",
      icon: <FileText size={16} />,
      category: "resumes",
      content: `
        <h3>ATS Optimization Tips</h3>
        <ul>
          <li><strong>Use Standard Sections:</strong> Experience, Education, Skills</li>
          <li><strong>Keyword Optimization:</strong> Include keywords from job descriptions</li>
          <li><strong>Simple Formatting:</strong> Avoid tables, columns, and graphics</li>
          <li><strong>Standard Fonts:</strong> Use Arial, Calibri, Times New Roman</li>
          <li><strong>File Format:</strong> Submit as .docx or PDF (text-based)</li>
          <li><strong>Header Names:</strong> Use standard section headers</li>
        </ul>
        
        <h3>Common ATS Mistakes to Avoid</h3>
        <ul>
          <li>Using images and graphics</li>
          <li>Creative formatting and layouts</li>
          <li>Headers and footers</li>
          <li>Uncommon file formats</li>
          <li>Missing contact information</li>
        </ul>
      `,
    },

    // Subscription
    {
      id: "understanding-premium-features",
      title: "Understanding Premium Features",
      description: "Learn about exclusive features for premium members",
      icon: <CreditCard size={16} />,
      category: "subscription",
      content: `
        <h3>Premium Features</h3>
        <ul>
          <li><strong>Advanced Analytics:</strong> See how your profile performs</li>
          <li><strong>Priority Application:</strong> Get seen by employers first</li>
          <li><strong>Enhanced Visibility:</strong> Higher in search results</li>
          <li><strong>Unlimited Applications:</strong> Apply to as many jobs as you want</li>
          <li><strong>Resume Review:</strong> Professional resume feedback</li>
          <li><strong>Direct Messaging:</strong> Contact recruiters directly</li>
          <li><strong>Salary Insights:</strong> See salary ranges for positions</li>
        </ul>
        
        <h3>Getting the Most from Premium</h3>
        <ul>
          <li>Complete your profile to 100%</li>
          <li>Use all available application slots</li>
          <li>Take advantage of resume reviews</li>
          <li>Enable all premium visibility settings</li>
        </ul>
      `,
    },

    // Technical Help
    {
      id: "browser-compatibility-guide",
      title: "Browser Compatibility Guide",
      description: "Supported browsers and optimal settings",
      icon: <Monitor size={16} />,
      category: "technical",
      content: `
        <h3>Supported Browsers</h3>
        <ul>
          <li><strong>Chrome:</strong> Version 90+ (Recommended)</li>
          <li><strong>Firefox:</strong> Version 88+</li>
          <li><strong>Safari:</strong> Version 14+</li>
          <li><strong>Edge:</strong> Version 90+</li>
        </ul>
        
        <h3>Optimal Browser Settings</h3>
        <ul>
          <li>Enable JavaScript</li>
          <li>Allow cookies</li>
          <li>Disable pop-up blockers for our domain</li>
          <li>Clear cache regularly for best performance</li>
          <li>Use latest browser version</li>
        </ul>
        
        <h3>Troubleshooting</h3>
        <ul>
          <li>Clear browser cache and cookies</li>
          <li>Disable browser extensions temporarily</li>
          <li>Try incognito/private mode</li>
          <li>Update your browser to latest version</li>
        </ul>
      `,
    },
  ];

  // Filter guides based on selected category and search term
  const filteredGuides = guides.filter(
    (guide) =>
      guide.category === selectedCategory &&
      (guide.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        guide.description.toLowerCase().includes(searchTerm.toLowerCase())),
  );

  const selectedGuideData = guides.find((guide) => guide.id === selectedGuide);

  // Navigation handlers
  const handleBackToGuides = () => {
    setSelectedGuide(null);
  };

  const handleGuideSelect = (guideId: string) => {
    setSelectedGuide(guideId);
  };

  const handleQuickLinkClick = (path: string) => {
    navigate(path);
  };

  if (selectedGuide) {
    return (
      <div
        className={`min-h-screen ${isDark ? "bg-gray-900 text-white" : "bg-gray-50 text-gray-900"}`}
      >
        <div className="max-w-4xl mx-auto p-6">
          <button
            onClick={handleBackToGuides}
            className={`flex items-center gap-2 mb-6 px-4 py-2 rounded-lg transition-colors ${
              isDark
                ? "bg-gray-800 hover:bg-gray-700 text-sky-300"
                : "bg-white hover:bg-gray-100 text-sky-600 border border-sky-200"
            }`}
          >
            <ArrowRight className="rotate-180" size={16} />
            Back to Guides
          </button>

          <div
            className={`rounded-lg p-6 border ${
              isDark ? "bg-gray-800 border-sky-800" : "bg-white border-sky-200"
            }`}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className={`p-2 rounded-lg ${
                  isDark
                    ? "bg-sky-900/30 text-sky-300"
                    : "bg-sky-100 text-sky-600"
                }`}
              >
                {selectedGuideData?.icon}
              </div>
              <h1 className="text-2xl font-bold text-sky-700 dark:text-sky-300">
                {selectedGuideData?.title}
              </h1>
            </div>
            <p
              className={`text-lg mb-6 ${isDark ? "text-gray-300" : "text-gray-600"}`}
            >
              {selectedGuideData?.description}
            </p>

            <div
              className={`prose max-w-none ${
                isDark ? "prose-invert" : ""
              } prose-headings:text-sky-700 prose-headings:dark:text-sky-300 prose-code:text-sky-600 prose-code:dark:text-sky-400`}
              dangerouslySetInnerHTML={{
                __html: selectedGuideData?.content || "",
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen ${isDark ? "bg-gray-900 text-white" : "bg-gray-50 text-gray-900"}`}
    >
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div
              className={`p-3 rounded-full bg-gradient-to-r from-sky-400 to-blue-500 text-white`}
            >
              <BookOpen size={32} />
            </div>
          </div>
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent">
            Help & Guides
          </h1>
          <p
            className={`text-xl max-w-2xl mx-auto ${
              isDark ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Comprehensive guides to help you make the most of our platform and
            accelerate your job search.
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Search guides..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-3 rounded-lg border focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-colors ${
                isDark
                  ? "bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                  : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
              }`}
            />
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Categories Sidebar */}
          <div className="lg:w-1/4">
            <div
              className={`rounded-lg p-6 border ${
                isDark
                  ? "bg-gray-800 border-sky-800"
                  : "bg-white border-sky-200"
              }`}
            >
              <h2 className="text-lg font-semibold mb-4 text-sky-700 dark:text-sky-300">
                Categories
              </h2>
              <div className="space-y-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-all ${
                      selectedCategory === category.id
                        ? "bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-lg"
                        : isDark
                          ? "hover:bg-gray-700 text-gray-300"
                          : "hover:bg-sky-50 text-gray-700 border border-transparent hover:border-sky-200"
                    }`}
                  >
                    <div
                      className={
                        selectedCategory === category.id
                          ? "text-white"
                          : "text-sky-500"
                      }
                    >
                      {category.icon}
                    </div>
                    <div>
                      <div className="font-medium">{category.title}</div>
                      <div
                        className={`text-sm ${
                          selectedCategory === category.id
                            ? "text-sky-100"
                            : isDark
                              ? "text-gray-400"
                              : "text-gray-500"
                        }`}
                      >
                        {category.description}
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Quick Links */}
              <div
                className={`mt-8 pt-6 border-t ${
                  isDark ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <h3 className="text-lg font-semibold mb-4 text-sky-700 dark:text-sky-300">
                  Quick Help
                </h3>
                <div className="space-y-2">
                  <button
                    onClick={() => handleQuickLinkClick("/contact-support")}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sky-600 dark:text-sky-400 hover:bg-sky-50 dark:hover:bg-sky-900/30 border border-transparent hover:border-sky-200 dark:hover:border-sky-800`}
                  >
                    <Mail size={16} />
                    Contact Support
                  </button>
                  <button
                    onClick={() => handleQuickLinkClick("/help-center")}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sky-600 dark:text-sky-400 hover:bg-sky-50 dark:hover:bg-sky-900/30 border border-transparent hover:border-sky-200 dark:hover:border-sky-800`}
                  >
                    <HelpCircle size={16} />
                    Help Center
                  </button>
                  <button
                    onClick={() => handleQuickLinkClick("/community")}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sky-600 dark:text-sky-400 hover:bg-sky-50 dark:hover:bg-sky-900/30 border border-transparent hover:border-sky-200 dark:hover:border-sky-800`}
                  >
                    <Users size={16} />
                    Community
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Guides List */}
          <div className="lg:w-3/4">
            <div
              className={`rounded-lg p-6 border ${
                isDark
                  ? "bg-gray-800 border-sky-800"
                  : "bg-white border-sky-200"
              }`}
            >
              <h2 className="text-xl font-semibold mb-6 text-sky-700 dark:text-sky-300">
                {categories.find((c) => c.id === selectedCategory)?.title}{" "}
                Guides
              </h2>

              {filteredGuides.length === 0 ? (
                <div className="text-center py-12">
                  <Search size={48} className="mx-auto mb-4 text-gray-400" />
                  <p className={isDark ? "text-gray-400" : "text-gray-500"}>
                    No guides found matching your search. Try different
                    keywords.
                  </p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {filteredGuides.map((guide) => (
                    <button
                      key={guide.id}
                      onClick={() => handleGuideSelect(guide.id)}
                      className={`w-full text-left p-4 rounded-lg border transition-all hover:shadow-lg ${
                        isDark
                          ? "bg-gray-700 border-sky-800 hover:bg-gray-600 hover:border-sky-700"
                          : "bg-white border-sky-200 hover:bg-sky-50 hover:border-sky-300"
                      } group`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div
                          className={`p-2 rounded-lg transition-colors ${
                            isDark
                              ? "bg-sky-900/30 group-hover:bg-sky-800/50 text-sky-300"
                              : "bg-sky-100 group-hover:bg-sky-200 text-sky-600"
                          }`}
                        >
                          {guide.icon}
                        </div>
                        <h3 className="font-semibold text-lg text-sky-700 dark:text-sky-300 group-hover:text-sky-600 dark:group-hover:text-sky-200">
                          {guide.title}
                        </h3>
                      </div>
                      <p className={isDark ? "text-gray-300" : "text-gray-600"}>
                        {guide.description}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Guides;
